import { z } from 'zod';

import { localeSchema } from './catalog.js';

/** Список через запятую: `brand=a,b`. */
const csv = z
  .string()
  .max(2000)
  .transform((v) =>
    v
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );

export const storefrontPathSchema = z.object({
  market: z.string().regex(/^[A-Za-z]{2}$/),
  locale: localeSchema,
});

/**
 * Параметры поиска и листинга категории. Фильтры атрибутов — `facet.<код>=v1,v2`
 * (Fastify разбирает query плоско, вложенные объекты не нужны).
 */
export const storefrontSearchSchema = z
  .object({
    q: z.string().trim().max(200).optional(),
    category: z.uuid().optional(),
    brand: csv.pipe(z.array(z.uuid()).max(50)).optional(),
    inStock: z
      .enum(['true', 'false'])
      .transform((v) => v === 'true')
      .optional(),
    priceMin: z.coerce.number().int().min(0).optional(),
    priceMax: z.coerce.number().int().min(0).optional(),
    facets: csv.pipe(z.array(z.string().regex(/^[a-z][a-z0-9_]{0,63}$/)).max(30)).optional(),
    sort: z.enum(['relevance', 'price_asc', 'price_desc', 'newest']).default('relevance'),
    page: z.coerce.number().int().min(1).max(1000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(24),
  })
  .catchall(z.string().max(2000))
  .transform(
    ({
      q,
      category,
      brand,
      inStock,
      priceMin,
      priceMax,
      facets,
      sort,
      page,
      pageSize,
      ...rest
    }) => {
      const facetFilters: Record<string, string[]> = {};
      for (const [key, value] of Object.entries(rest)) {
        const code = /^facet\.([a-z][a-z0-9_]{0,63})$/.exec(key)?.[1];
        if (code && typeof value === 'string') {
          facetFilters[code] = value
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .slice(0, 50);
        }
      }
      return {
        q: q || undefined,
        categoryId: category,
        brandIds: brand,
        inStock,
        priceMin,
        priceMax,
        facets: Object.keys(facetFilters).length ? facetFilters : undefined,
        facetCodes: facets,
        sort,
        page,
        pageSize,
      };
    },
  );
export type StorefrontSearchQuery = z.output<typeof storefrontSearchSchema>;
